import { Button } from "@moe/oss/ui/button";
import React, { useCallback, useRef, useState } from "react";
import ReactCrop, { centerCrop, Crop, makeAspectCrop, PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

interface Props {
  src: string;
  aspect: number;
  minWidth: number;
  minHeight: number;
  onCropComplete: (croppedImage: Blob) => void;
  onClose: () => void;
}

function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 100
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

export function ImageCropModal({ src, aspect, minWidth, minHeight, onCropComplete, onClose }: Props) {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const imgRef = useRef<HTMLImageElement>(null);

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspect));
    },
    [aspect]
  );

  const cropImage = useCallback(async () => {
    if (completedCrop && imgRef.current) {
      const image = imgRef.current;
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      const canvas = document.createElement("canvas");
      canvas.width = completedCrop.width;
      canvas.height = completedCrop.height;

      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("No 2d context");

      ctx.drawImage(
        image,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
        0,
        0,
        completedCrop.width,
        completedCrop.height
      );

      canvas.toBlob(
        (blob) => {
          if (blob) {
            onCropComplete(blob);
          }
        },
        "image/jpeg",
        1
      );
    }
  }, [completedCrop, onCropComplete]);

  return (
    <div className="bg-float border-line scroll-hidden flex w-full flex-col space-y-5 overflow-auto rounded-lg px-6 py-5 focus:outline-none md:max-h-[32rem] md:max-w-[32rem] md:border">
      <div className="flex w-full shrink-0 justify-end space-x-4">
        <Button variant="clear" onClick={onClose} className="">
          Reset
        </Button>
        <Button onClick={cropImage} className="" autofocus>
          Save
        </Button>
      </div>
      <ReactCrop
        className="mt-10"
        crop={crop}
        minWidth={minWidth}
        minHeight={minHeight}
        onChange={(_, percentCrop) => setCrop(percentCrop)}
        onComplete={(c) => setCompletedCrop(c)}
        aspect={aspect}
      >
        <img ref={imgRef} src={src} onLoad={onImageLoad} alt="Crop me" />
      </ReactCrop>
    </div>
  );
}
